import {
  Box,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  PlayArrow as BuildIcon,
  DeleteSweep as ClearIcon,
  Refresh as RefreshIcon,
  RocketLaunch as PublishIcon,
  Apps as AppsIcon,
} from "@mui/icons-material";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {ConfirmDialog, StatusChip, FilterDropdown} from "../componenets/ui";
import {useMessageContext} from "../contexts/MessageContext";
import {t} from "../componenets/dashboard/coach/coachTokens";
import {
  getBrands,
  getCurrentStatus,
  getQueueDepth,
  purgeQueue,
  triggerBuild,
  triggerRelease,
} from "../services/system/BuildConsoleService";
import moment from "moment";
import "moment/locale/he";

moment.locale("he");

const PLATFORM_OPTS = [
  {value: "both", label: "iOS + Android"},
  {value: "ios", label: "iOS"},
  {value: "android", label: "Android"},
];

// Build-status timestamps (startedAt/finishedAt/createdDate) come back from the
// API as either an epoch number (Jackson serializes Instant as fractional
// seconds) or an ISO string. Normalize to a moment so sorting and display work
// regardless — and never assume .localeCompare (it crashes on numbers).
const toMoment = (v) => {
  if (v == null || v === "") return null;
  if (typeof v === "number") return moment.unix(v); // epoch seconds
  return moment(v); // ISO string
};
// Sort key: epoch millis, missing → 0 (sorts last when descending).
const toMillis = (v) => {
  const m = toMoment(v);
  return m && m.isValid() ? m.valueOf() : 0;
};

const td = {padding: "10px 12px", borderBottom: `1px solid ${t.borderLight}`, fontSize: "12.5px", color: t.text, verticalAlign: "middle"};
const th = {padding: "10px 12px", fontSize: "10.5px", fontWeight: 700, color: t.t3, borderBottom: `1px solid ${t.border}`,
            background: t.bg, whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: ".06em"};

const POLL_MS = 10000;

// One status cell: in-progress / success / failed / never.
function StatusCell({s}) {
  if (!s) return <Typography sx={{fontSize: "12px", color: t.t3}}>—</Typography>;
  const st = (s.status || "").toUpperCase();
  if (st === "STARTED") return <StatusChip label="רץ…" variant="info" size="small" />;
  if (st === "SUCCESS") return <StatusChip label={`✓ ${s.versionName || ""}`.trim()} variant="success" size="small" />;
  if (st === "FAILED") return <StatusChip label="נכשל" variant="error" size="small" />;
  return <StatusChip label={st} variant="default" size="small" />;
}

// Pill button matching the Clients "לקוח חדש" CTA. variant: primary (green) |
// ghost (outlined) | danger (outlined red).
function PillButton({children, icon, onClick, disabled, title, variant = "primary"}) {
  const base = {
    display: "inline-flex", alignItems: "center", gap: "5px",
    height: 34, px: "15px", borderRadius: t.rs,
    fontSize: "13px", fontWeight: 600, letterSpacing: "-0.1px",
    cursor: disabled ? "not-allowed" : "pointer", fontFamily: "inherit",
    transition: "all .14s", opacity: disabled ? 0.5 : 1, whiteSpace: "nowrap",
  };
  const styles = {
    primary: {...base, background: t.green, color: "#fff", border: "none",
      "&:hover": disabled ? {} : {background: t.greenDark, transform: "translateY(-1px)", boxShadow: "0 4px 12px rgba(1,187,167,.35)"}},
    ghost: {...base, background: t.white, color: t.greenText, border: `1.5px solid ${t.green}`,
      "&:hover": disabled ? {} : {background: t.greenLight}},
    danger: {...base, background: t.white, color: t.redText, border: `1.5px solid ${t.red}`,
      "&:hover": disabled ? {} : {background: t.redLight}},
  };
  return (
    <Box component="button" onClick={disabled ? undefined : onClick} disabled={disabled} title={title} sx={styles[variant]}>
      {icon}{children}
    </Box>
  );
}

// Small stat tile in the KPI row (mirrors Clients KPI look).
function StatTile({iconBg, iconColor, value, valueColor, label, sub}) {
  return (
    <Box sx={{
      background: t.white, border: `1px solid ${t.border}`, borderRadius: t.r,
      boxShadow: t.sh, padding: "14px 16px",
      display: "flex", alignItems: "center", gap: "12px",
    }}>
      <Box sx={{
        width: 42, height: 42, borderRadius: "10px", flexShrink: 0,
        display: "flex", alignItems: "center", justifyContent: "center",
        background: iconBg, color: iconColor,
      }}>
        <AppsIcon sx={{fontSize: 22}}/>
      </Box>
      <Box sx={{minWidth: 0}}>
        <Typography sx={{fontSize: "22px", fontWeight: 800, color: valueColor, lineHeight: 1.1}}>{value}</Typography>
        <Typography sx={{fontSize: "12px", fontWeight: 600, color: t.text}}>{label}</Typography>
        {sub && <Typography sx={{fontSize: "11px", color: t.t3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>{sub}</Typography>}
      </Box>
    </Box>
  );
}

const BuildConsolePage = () => {
  const {notificationHandler} = useMessageContext();
  const [depth, setDepth] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [brands, setBrands] = useState([]);
  const [platform, setPlatform] = useState("both");
  const [brandSel, setBrandSel] = useState("__all__"); // "__all__" or a bundleId
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null); // {kind, title, message, action}
  const pollRef = useRef(null);

  // HttpService returns the raw Axios response; the payload is in res.data.
  const asArray = (v) => (Array.isArray(v) ? v : Array.isArray(v?.body) ? v.body : []);

  const load = useCallback(async () => {
    try {
      const [d, cur] = await Promise.all([getQueueDepth(), getCurrentStatus()]);
      const depthData = d?.data ?? {};
      setDepth(depthData.depth ?? depthData.body?.depth ?? 0);
      setStatuses(asArray(cur?.data));
    } catch (e) {
      // keep last values; the grid stays on whatever we had
    }
  }, []);

  useEffect(() => {
    getBrands().then((b) => setBrands(asArray(b?.data))).catch(() => {});
    load();
    pollRef.current = setInterval(load, POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [load]);

  // Latest status per "brand|platform" -> map for the grid.
  const statusMap = useMemo(() => {
    const m = {};
    for (const s of statuses) m[`${s.brand}|${s.platform}`] = s;
    return m;
  }, [statuses]);

  const runningJob = useMemo(
    () => statuses.find((s) => (s.status || "").toUpperCase() === "STARTED"),
    [statuses],
  );

  // Brand options for the FilterDropdown: "all" + every registered brand.
  const brandOpts = useMemo(
    () => [
      {value: "__all__", label: "כל המותגים"},
      ...brands.map((b) => ({value: b.bundleId, label: b.appName || b.bundleId})),
    ],
    [brands],
  );

  const doTrigger = async () => {
    setBusy(true);
    try {
      const brandsArg = brandSel === "__all__" ? null : [brandSel];
      const res = await triggerBuild({brands: brandsArg, platform});
      const n = res?.data?.enqueued ?? "?";
      notificationHandler.success(`נשלחו ${n} בנייה/ות לתור`);
      load();
    } catch (e) {
      notificationHandler.error("נכשל לשלוח בנייה");
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  // Publish (release) is ALWAYS per single brand — never "all" — because it
  // submits the brand for store review (iOS) / production (Android).
  const doRelease = async () => {
    setBusy(true);
    try {
      const res = await triggerRelease({brands: [brandSel], platform});
      const n = res?.data?.enqueued ?? "?";
      notificationHandler.success(`נשלחו ${n} פרסום/ים לתור`);
      load();
    } catch (e) {
      notificationHandler.error("נכשל לשלוח פרסום");
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  const doPurge = async () => {
    setBusy(true);
    try {
      await purgeQueue();
      notificationHandler.success("התור נוקה");
      load();
    } catch (e) {
      notificationHandler.error("נכשל לנקות את התור");
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  const onBuildClick = () => {
    const scope = brandSel === "__all__" ? "כל המותגים" : (brands.find((b) => b.bundleId === brandSel)?.appName || brandSel);
    const platLabel = platform === "both" ? "iOS + Android" : platform;
    // Confirm for 'all' (big fan-out); fire directly for a single brand.
    if (brandSel === "__all__") {
      setConfirm({
        title: "בניית כל המותגים?",
        message: `יישלחו בניות עבור ${scope} (${platLabel}). זה הרבה דקות בנייה.`,
        confirmText: "בנה",
        action: doTrigger,
      });
    } else {
      doTrigger();
    }
  };

  const onPublishClick = () => {
    if (brandSel === "__all__") return; // guarded by disabled button; defensive
    const scope = brands.find((b) => b.bundleId === brandSel)?.appName || brandSel;
    const platLabel = platform === "both" ? "iOS + Android" : platform;
    // Publish always confirms — it submits to store review / production.
    setConfirm({
      title: `לפרסם את ${scope}?`,
      message: `${scope} (${platLabel}) יישלח לסקירת חנות (iOS) / ייצור (Android). פעולה זו מגישה את האפליקציה לאישור.`,
      confirmText: "פרסם",
      action: doRelease,
    });
  };

  const onClearClick = () =>
    setConfirm({
      title: "לנקות את התור?",
      message: "כל הבניות הממתינות יוסרו מהתור (בניות שכבר רצות לא יושפעו).",
      confirmText: "נקה",
      variant: "danger",
      action: doPurge,
    });

  const selectedLabel = brandSel === "__all__"
    ? "כל המותגים"
    : (brands.find((b) => b.bundleId === brandSel)?.appName || brandSel);

  return (
    <Box sx={{display: "flex", flexDirection: "column", gap: "18px", direction: "rtl"}}>
      {/* ─── Title row (on canvas, like Clients) ─── */}
      <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0}}>
        <Box>
          <Typography sx={{fontSize: {xs: "22px", md: "26px"}, fontWeight: 800, color: t.text, lineHeight: 1.2, letterSpacing: "-0.3px"}}>
            בנייה ופרסום
          </Typography>
          <Typography sx={{fontSize: "13px", color: t.t2, mt: 0.25}}>בנייה ופרסום של אפליקציות מותג לבן</Typography>
        </Box>
        <Box sx={{display: "flex", alignItems: "center", gap: "8px"}}>
          <IconButton onClick={load} size="small" sx={{color: t.t2}}><RefreshIcon sx={{fontSize: 20}}/></IconButton>
          <PillButton icon={<BuildIcon sx={{fontSize: 16}}/>} disabled={busy} onClick={onBuildClick}>
            בנה {selectedLabel}
          </PillButton>
        </Box>
      </Box>

      {/* ─── Queue / status stat cards (KPI-style like Clients) ─── */}
      <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr 1fr", md: "repeat(3, 1fr)"}, gap: "14px", flexShrink: 0}}>
        <StatTile
          iconBg={depth > 0 ? t.amberLight : t.greenLight} iconColor={depth > 0 ? t.amber : t.green}
          value={depth ?? "…"} valueColor={depth > 0 ? t.amber : t.green}
          label="בתור"
          sub={depth > 0 ? "ממתינות לבנייה" : "התור ריק"}
        />
        <StatTile
          iconBg={runningJob ? t.blueLight : t.bg} iconColor={runningJob ? t.blue : t.t3}
          value={runningJob ? "1" : "0"} valueColor={runningJob ? t.blue : t.t3}
          label="רצות כעת"
          sub={runningJob ? `${runningJob.brand?.split(".").pop()} · ${runningJob.platform}` : "אין בנייה פעילה"}
        />
        <StatTile
          iconBg={t.greenLight} iconColor={t.green}
          value={brands.length || "…"} valueColor={t.text}
          label="מותגים"
          sub="רשומים במערכת"
        />
      </Box>

      {/* ─── Table card (white/bordered/shadow, like Clients) ─── */}
      <Box sx={{
        background: t.white, border: `1px solid ${t.border}`, borderRadius: t.r,
        boxShadow: t.sh, overflow: "hidden",
        display: "flex", flexDirection: "column",
      }}>
        {/* Filter / action bar inside the card */}
        <Box sx={{
          display: "flex", alignItems: "center", gap: "7px",
          padding: "10px 14px", borderBottom: `1px solid ${t.border}`,
          background: t.bg, flexWrap: "wrap", flexShrink: 0,
        }}>
          {/* Count pill */}
          <Box sx={{
            display: "inline-flex", alignItems: "center", gap: "4px",
            fontSize: "12px", fontWeight: 700, color: t.greenText,
            background: t.greenLight, border: "1px solid rgba(1,187,167,.2)",
            padding: "4px 12px", borderRadius: "20px", whiteSpace: "nowrap",
          }}>
            <AppsIcon sx={{fontSize: 14}}/>
            {brands.length} מותגים
          </Box>

          <FilterDropdown label="פלטפורמה" value={platform} onChange={setPlatform} options={PLATFORM_OPTS} minWidth={130}/>
          <FilterDropdown label="מותג" value={brandSel} onChange={setBrandSel} options={brandOpts} minWidth={170}/>

          <Box sx={{flex: 1}}/>

          {/* Publish (per-brand) + Clear queue */}
          <PillButton
            variant="ghost"
            icon={<PublishIcon sx={{fontSize: 15}}/>}
            disabled={busy || brandSel === "__all__"}
            onClick={onPublishClick}
            title={brandSel === "__all__" ? "בחר מותג ספציפי כדי לפרסם" : undefined}
          >
            פרסם
          </PillButton>
          <PillButton variant="danger" icon={<ClearIcon sx={{fontSize: 15}}/>} disabled={busy} onClick={onClearClick}>
            נקה תור
          </PillButton>
        </Box>

        {/* Per-brand status table */}
        {brands.length === 0 ? (
          <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", minHeight: 240}}>
            <CircularProgress size={28} sx={{color: t.green}}/>
          </Box>
        ) : (
          <TableContainer sx={{overflowY: "auto", maxHeight: 600}}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{...th, textAlign: "right"}}>מותג</TableCell>
                  <TableCell sx={{...th, textAlign: "center", width: 140}}>iOS</TableCell>
                  <TableCell sx={{...th, textAlign: "center", width: 140}}>Android</TableCell>
                  <TableCell sx={{...th, textAlign: "right", width: 140}}>ריצה אחרונה</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {brands.map((b) => {
                  const ios = statusMap[`${b.bundleId}|ios`];
                  const and = statusMap[`${b.bundleId}|android`];
                  const last = [ios, and].filter(Boolean).sort((a, c) =>
                    toMillis(c.finishedAt || c.startedAt) - toMillis(a.finishedAt || a.startedAt))[0];
                  const lastM = last && toMoment(last.finishedAt || last.startedAt);
                  return (
                    <TableRow key={b.bundleId}
                      onClick={() => setBrandSel(b.bundleId)}
                      sx={{
                        cursor: "pointer",
                        bgcolor: brandSel === b.bundleId ? `${t.green}14` : "transparent",
                        "&:hover td": {background: t.greenLight},
                        transition: "background .12s ease",
                      }}>
                      <TableCell sx={{...td, textAlign: "right", fontWeight: 600}}>{b.appName || b.bundleId}</TableCell>
                      <TableCell sx={{...td, textAlign: "center"}}><StatusCell s={ios} /></TableCell>
                      <TableCell sx={{...td, textAlign: "center"}}><StatusCell s={and} /></TableCell>
                      <TableCell sx={{...td, textAlign: "right", color: t.t2, fontSize: "12px"}}>
                        {lastM && lastM.isValid() ? lastM.fromNow() : "—"}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <ConfirmDialog
        open={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={() => confirm?.action?.()}
        title={confirm?.title}
        description={confirm?.message}
        confirmText={confirm?.confirmText}
        variant={confirm?.variant || "warning"}
      />
    </Box>
  );
};

export default BuildConsolePage;
